/* eslint-disable import/max-dependencies */
import {
  getTableFilters,
  getTableSorting,
  getTablePage,
  getTablePageSize,
  getTablePendingFields,
} from "@fas/cpa-state-manager/services/selectors/table";
import { changeTableFilters } from "@fas/cpa-state-manager/redux/actions/table";
import { useSelector, useDispatch } from "react-redux";

import { useHandleRequest } from "@fas/cpa-state-manager/redux/hooks";
import { getRequestService } from "@fas/cpa-state-manager/services/request";
import type {
  Filters,
  Sorting,
} from "@fas/cpa-state-manager/redux/actions/table/actions";
import { useEffect } from "react";
import { getLoadingState } from "@fas/cpa-state-manager/services/selectors/loading";
import ToolTip from "../../components/Reports/Tooltip";
import { isEqualPreviousBuilder } from "./utils";
import { MAP_API_TIMEOUT } from "../../constants";

type FetchDataArgs = {
  filters: Filters;
  sorting: Sorting;
  page?: number;
  limit?: number;
  fields: ReadonlyArray<
    | string
    | {
        fieldName: string;
        name: string;
      }
  >;
  path: string;
};
export type TableResponse<T> = {
  data: T;
  headers: any;
};
export type UseTableFiltersArgs = {
  tableKey: string;
};
export type UseTableFiltersHook = {
  isLoading?: boolean;
  disableApply?: boolean;
  filters: any;
  onChange: (arg0: any) => unknown;
  onApply: () => unknown;
  onDeleteAll: () => unknown;
  onDelete: (arg0: any) => unknown;
};
export function fetchData<T>({
  filters,
  sorting,
  page,
  limit,
  fields,
  path,
}: FetchDataArgs): Promise<TableResponse<T>> {
  const {
    date: { from, to } = { from: "", to: "" },
    groupBy,
    ...restFilters
  } = filters as any;
  const [[current, direction] = []] = Object.entries(sorting);
  return getRequestService()
    .post(
      path,
      {
        filters: Object.keys(restFilters)
          .filter((key) => {
            const value = filters[key as keyof typeof filters] as string | string[];
            return Array.isArray(value) ? !!value.length : !!value;
          })
          .reduce(
            (acc: any, key: string) => {
              const value = filters[key as keyof typeof filters] as string | string[];
              return {
                ...acc,
                [key]: typeof value === "string" ? [value.trim()] : value,
              };
            },
            {
              date: [
                {
                  from,
                  to,
                },
              ],
            }
          ),
        group: groupBy ? [groupBy] : undefined,
        sorting: current
          ? [
            {
              current,
              direction,
            },
          ]
          : undefined,
        fields,
        page,
        limit,
      },
      {
        timeout: MAP_API_TIMEOUT,
      }
    )
    .then(({ data, headers }) => ({
      data,
      headers,
    }));
}
const tablesCompareCache: Record<string, (arg0: any) => boolean> = {};
export function isEqualPrevious(tableKey: string, data: any): boolean {
  tablesCompareCache[tableKey] =
    tablesCompareCache[tableKey] || isEqualPreviousBuilder();
  return !!tablesCompareCache[tableKey]?.(data);
}
const defaultSorting: Sorting = {
  "": "asc",
};
export function getAllowedSorting(sorting: Sorting, fields: string[]): Sorting {
  const [[current = ""] = []]: any = Object.entries(sorting);
  return fields.includes(current) ? sorting : defaultSorting;
}
export const useApplyFilters = ({ tableKey }: UseTableFiltersArgs) => {
  const dispatch = useDispatch();
  return () =>
    dispatch({
      type: tableKey,
    });
};

function getClearFilters(tableFilters: Filters): Filters {
  const canClear: (arg0: string) => boolean = (key: string): boolean =>
    !["date", "groupBy"].includes(key);

  return Object.keys(tableFilters).reduce(
    (acc: Filters, key: string): Filters =>
      ({
        ...acc,
        [key]: canClear(key) ? "" : tableFilters[key as keyof typeof tableFilters],
      }) as Filters,
    {}
  );
}

export const useTableFilters: (
  arg0: UseTableFiltersArgs,
) => UseTableFiltersHook = ({ tableKey }) => {
  const dispatch = useDispatch();
  const { isLoading } = useHandleRequest(tableKey);
  const isLoadingExport = useSelector((state) =>
    getLoadingState(state, `${tableKey}-export`)
  );
  const tableFilters = useSelector((state) => getTableFilters(state, tableKey));
  const tableSorting = useSelector((state) => getTableSorting(state, tableKey));
  const page = useSelector((state) => getTablePage(state, tableKey));
  const limit = useSelector((state) => getTablePageSize(state, tableKey));
  const pendingFields = useSelector((state) =>
    getTablePendingFields(state, tableKey)
  );
  const filters = Object.keys(tableFilters)
    .filter(
      (key) => !["groupBy"].includes(key) && isValidFilter(tableFilters[key as keyof typeof tableFilters])
    )
    .reduce((acc, key) => ({ ...acc, [key]: tableFilters[key as keyof typeof tableFilters] }), {});
  const onApply = useApplyFilters({
    tableKey,
  });
  useEffect(() => {
    if (
      !isEqualPrevious(tableKey, {
        tableSorting,
        page,
        limit,
      })
    ) {
      onApply();
    }
  }, [tableKey, tableSorting, page, limit]);

  function isValidFilter(filter: string | number | Array<string> | undefined) {
    return Array.isArray(filter) ? !!filter.length : !!filter;
  }

  return {
    isLoading: isLoading || isLoadingExport,
    disableApply: pendingFields.length < 2,
    filters,
    onApply,
    onChange: (newFilters) => {
      dispatch(
        changeTableFilters(tableKey, { ...tableFilters, ...newFilters })
      );
      onApply();
    },
    onDeleteAll: () => {
      dispatch(changeTableFilters(tableKey, getClearFilters(tableFilters)));
    },
    onDelete: ({ date, ...newFilters }) => {
      dispatch(
        changeTableFilters(tableKey, {
          ...tableFilters,
          ...newFilters,
          ...(date
            ? {
              date,
            }
            : {}), // don't clear require filter
        })
      );
    },
    WrapChipLabel: ToolTip,
  };
};
